import * as Phaser from 'phaser';
import { NavigationBar } from '../objects/NavigationBar';
import { transitionManager } from '../systems/TransitionManager';
import { soundManager } from '../systems/SoundManager';
import { DESIGN } from '../config/design';
import { eventTracker } from '../analytics/EventTracker';

export class AnalyticsDashboardScene extends Phaser.Scene {
  private metrics = {
    firstGrabCompletion: 68.5,
    immediateRetryRate: 42.1,
    day1Return: 35.8,
    avgSessionLength: '14m 22s',
    dataHealth: 96.4,
    validSessions: '1.2M',
    suspectedSessions: '45.2K',
    filteredEvents: '12.8K',
  };

  constructor() {
    super({ key: 'AnalyticsDashboardScene' });
  }

  create(): void {
    this.addBackground();
    this.addHeader();
    this.addNavigation();
    this.addKpiCards();
    this.addVariantComparison();
    this.addDataQuality();
    this.addDecorativeCards();

    eventTracker.track('analytics_view', {});
  }

  private addBackground(): void {
    const graphics = this.add.graphics();
    for (let y = 0; y < 720; y += 4) {
      const t = y / 720;
      const r = Math.floor(248 + (232 - 248) * t);
      const g = Math.floor(249 + (240 - 249) * t);
      const b = Math.floor(255 + (244 - 255) * t);
      graphics.fillStyle((r << 16) | (g << 8) | b, 1);
      graphics.fillRect(0, y, 1280, 4);
    }
  }

  private addHeader(): void {
    this.add.text(40, 90, 'Internal Analytics', {
      fontFamily: DESIGN.fonts.headline,
      fontSize: '36px',
      color: DESIGN.colors.onSurface,
      fontStyle: 'bold',
    }).setOrigin(0, 0.5);

    this.add.text(40, 130, 'Real-time performance tracking for Toybox Arcade Engine', {
      fontFamily: DESIGN.fonts.body,
      fontSize: '16px',
      color: DESIGN.colors.onSurfaceVariant,
    }).setOrigin(0, 0.5);

    const periodBadge = this.add.container(1150, 110);
    const badgeBg = this.add.rectangle(0, 0, 150, 34, parseInt(DESIGN.colors.surfaceContainerHigh.replace('#', '0x'), 16));
    badgeBg.setStrokeStyle(1, parseInt(DESIGN.colors.outlineVariant.replace('#', '0x'), 16));
    periodBadge.add(badgeBg);
    periodBadge.add(this.add.text(0, 0, '📅 Last 30 Days', {
      fontFamily: DESIGN.fonts.body,
      fontSize: '12px',
      color: DESIGN.colors.onSurface,
      fontStyle: 'bold',
    }).setOrigin(0.5));
  }

  private addNavigation(): void {
    new NavigationBar(this, 0, 35, 'analytics', (key) => this.handleNavigation(key));
  }

  private addKpiCards(): void {
    const kpis = [
      { label: 'First Grab Completion', value: `${this.metrics.firstGrabCompletion}%`, trend: '+2.4%', trendUp: true, icon: '🎯', color: DESIGN.colors.primaryContainer },
      { label: 'Immediate Retry Rate', value: `${this.metrics.immediateRetryRate}%`, trend: '-1.2%', trendUp: false, icon: '🔄', color: DESIGN.colors.secondaryFixed },
      { label: 'Day 1 Return', value: `${this.metrics.day1Return}%`, trend: '+5.1%', trendUp: true, icon: '✅', color: DESIGN.colors.tertiaryFixed },
      { label: 'Avg. Session Length', value: this.metrics.avgSessionLength, trend: 'Steady', trendUp: true, icon: '⏱️', color: DESIGN.colors.surfaceContainerHigh },
    ];

    const cardW = 260;
    const cardH = 130;
    const startX = 70;
    const gap = 40;

    kpis.forEach((kpi, index) => {
      const x = startX + index * (cardW + gap);
      const y = 200;
      this.createKpiCard(x, y, cardW, cardH, kpi);
    });
  }

  private createKpiCard(
    x: number,
    y: number,
    w: number,
    h: number,
    kpi: { label: string; value: string; trend: string; trendUp: boolean; icon: string; color: string }
  ): void {
    const card = this.add.container(x, y);

    const shadow = this.add.rectangle(6, 6, w, h, 0x000000, 0.08);
    card.add(shadow);

    const bg = this.add.rectangle(0, 0, w, h, 0xffffff, 1);
    bg.setStrokeStyle(1, parseInt(DESIGN.colors.outlineVariant.replace('#', '0x'), 16));
    card.add(bg);

    const iconBg = this.add.circle(-w / 2 + 40, -h / 2 + 40, 22, parseInt(kpi.color.replace('#', '0x'), 16));
    card.add(iconBg);
    card.add(this.add.text(-w / 2 + 40, -h / 2 + 40, kpi.icon, { fontSize: '22px' }).setOrigin(0.5));

    const trendColor = kpi.trendUp ? DESIGN.colors.secondary : DESIGN.colors.fail;
    card.add(this.add.text(w / 2 - 20, -h / 2 + 30, kpi.trend, {
      fontFamily: DESIGN.fonts.body,
      fontSize: '14px',
      color: trendColor,
      fontStyle: 'bold',
    }).setOrigin(1, 0.5));

    card.add(this.add.text(-w / 2 + 20, h / 2 - 45, kpi.label.toUpperCase(), {
      fontFamily: DESIGN.fonts.body,
      fontSize: '11px',
      color: DESIGN.colors.onSurfaceVariant,
      fontStyle: 'bold',
    }).setOrigin(0, 0.5));

    card.add(this.add.text(-w / 2 + 20, h / 2 - 15, kpi.value, {
      fontFamily: DESIGN.fonts.headline,
      fontSize: '30px',
      color: kpi.trendUp ? DESIGN.colors.primary : DESIGN.colors.onSurface,
      fontStyle: 'bold',
    }).setOrigin(0, 0.5));

    bg.setInteractive({ useHandCursor: true });
    bg.on('pointerdown', () => {
      soundManager.play('click');
    });
  }

  private addVariantComparison(): void {
    const container = this.add.container(70, 410);
    const w = 760;
    const h = 340;

    const shadow = this.add.rectangle(6, 6, w, h, 0x000000, 0.08);
    container.add(shadow);

    const bg = this.add.rectangle(0, 0, w, h, 0xffffff, 1);
    bg.setStrokeStyle(1, parseInt(DESIGN.colors.outlineVariant.replace('#', '0x'), 16));
    container.add(bg);

    // Header
    container.add(this.add.text(-w / 2 + 20, -h / 2 + 25, '🧪 Variant Performance Comparison', {
      fontFamily: DESIGN.fonts.headline,
      fontSize: '20px',
      color: DESIGN.colors.onSurface,
      fontStyle: 'bold',
    }).setOrigin(0, 0.5));

    const activeBadge = this.add.container(w / 2 - 80, -h / 2 + 25);
    activeBadge.add(this.add.rectangle(0, 0, 90, 26, parseInt(DESIGN.colors.secondary.replace('#', '0x'), 16)));
    activeBadge.add(this.add.text(0, 0, 'Active Test', {
      fontFamily: DESIGN.fonts.body,
      fontSize: '11px',
      color: '#ffffff',
      fontStyle: 'bold',
    }).setOrigin(0.5));
    container.add(activeBadge);

    // Table headers
    const headers = ['Variant Name', 'Completion', 'Retry Rate', 'Near Miss', 'Status'];
    const colX = [-w / 2 + 30, -60, 80, 200, 320];
    headers.forEach((header, i) => {
      container.add(this.add.text(colX[i], -h / 2 + 75, header, {
        fontFamily: DESIGN.fonts.body,
        fontSize: '11px',
        color: DESIGN.colors.onSurfaceVariant,
        fontStyle: 'bold',
      }).setOrigin(i === 0 ? 0 : 0.5, 0.5));
    });

    // Rows
    const rows = [
      { name: 'Version A (Baseline)', completion: '62.1%', retry: '45.0%', nearMiss: '18.2%', status: 'Control', statusColor: '#8a7170' },
      { name: 'Version B (Soft Grip)', completion: '71.4%', retry: '38.2%', nearMiss: '14.5%', status: 'WINNER', statusColor: DESIGN.colors.secondary, highlight: true },
      { name: 'Version C (High Tension)', completion: '58.9%', retry: '49.4%', nearMiss: '22.1%', status: 'UNDERPERFORMING', statusColor: DESIGN.colors.fail },
    ];

    rows.forEach((row, index) => {
      const y = -h / 2 + 120 + index * 55;
      const rowBg = this.add.rectangle(0, y, w - 24, 50, row.highlight ? parseInt(DESIGN.colors.secondaryContainer.replace('#', '0x'), 16) : 0xffffff, row.highlight ? 0.3 : 0);
      container.add(rowBg);

      const values = [row.name, row.completion, row.retry, row.nearMiss];
      values.forEach((value, i) => {
        container.add(this.add.text(colX[i], y, value, {
          fontFamily: DESIGN.fonts.body,
          fontSize: i === 0 ? '14px' : '13px',
          color: i === 0 ? DESIGN.colors.onSurface : DESIGN.colors.onSurfaceVariant,
          fontStyle: i === 0 || row.highlight ? 'bold' : 'normal',
        }).setOrigin(i === 0 ? 0 : 0.5, 0.5));
      });

      const statusBadge = this.add.container(colX[4], y);
      const statusColor = parseInt(row.statusColor.replace('#', '0x'), 16);
      const badgeWidth = row.status === 'Control' ? 70 : 130;
      statusBadge.add(this.add.rectangle(0, 0, badgeWidth, 24, statusColor, 0.15));
      const badgeText = this.add.text(0, 0, row.status, {
        fontFamily: DESIGN.fonts.body,
        fontSize: '10px',
        color: row.statusColor,
        fontStyle: 'bold',
      }).setOrigin(0.5);
      statusBadge.add(badgeText);
      container.add(statusBadge);
    });

    // Actions
    container.add(this.add.text(w / 2 - 20, h / 2 - 30, 'View Detailed Logs', {
      fontFamily: DESIGN.fonts.body,
      fontSize: '12px',
      color: DESIGN.colors.onSurfaceVariant,
    }).setOrigin(1, 0.5));

    const applyBtn = this.add.rectangle(w / 2 - 180, h / 2 - 30, 150, 32, parseInt(DESIGN.colors.secondary.replace('#', '0x'), 16));
    applyBtn.setInteractive({ useHandCursor: true });
    container.add(applyBtn);
    container.add(this.add.text(w / 2 - 180, h / 2 - 30, 'Apply Variant B', {
      fontFamily: DESIGN.fonts.body,
      fontSize: '12px',
      color: '#ffffff',
      fontStyle: 'bold',
    }).setOrigin(0.5));

    applyBtn.on('pointerdown', () => {
      soundManager.play('click');
      eventTracker.track('analytics_apply_variant', { variant: 'B' });
    });
  }

  private addDataQuality(): void {
    const container = this.add.container(990, 410);
    const w = 340;
    const h = 340;

    const shadow = this.add.rectangle(6, 6, w, h, 0x000000, 0.08);
    container.add(shadow);

    const bg = this.add.rectangle(0, 0, w, h, 0xffffff, 1);
    bg.setStrokeStyle(1, parseInt(DESIGN.colors.outlineVariant.replace('#', '0x'), 16));
    container.add(bg);

    container.add(this.add.text(-w / 2 + 20, -h / 2 + 25, '📊 Data Quality', {
      fontFamily: DESIGN.fonts.headline,
      fontSize: '20px',
      color: DESIGN.colors.onSurface,
      fontStyle: 'bold',
    }).setOrigin(0, 0.5));

    const dot = this.add.circle(w / 2 - 30, -h / 2 + 25, 5, parseInt(DESIGN.colors.secondary.replace('#', '0x'), 16));
    container.add(dot);
    this.tweens.add({
      targets: dot,
      scale: 1.2,
      alpha: 0.5,
      duration: 1000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // Circular gauge
    const gaugeY = -h / 2 + 130;
    const radius = 70;
    const track = this.add.graphics();
    track.lineStyle(12, parseInt(DESIGN.colors.surfaceContainerHigh.replace('#', '0x'), 16));
    track.beginPath();
    track.arc(0, gaugeY, radius, 0, Math.PI * 2);
    track.strokePath();
    container.add(track);

    const progress = this.metrics.dataHealth / 100;
    const fill = this.add.graphics();
    fill.lineStyle(12, parseInt(DESIGN.colors.secondary.replace('#', '0x'), 16));
    fill.beginPath();
    fill.arc(0, gaugeY, radius, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * progress);
    fill.strokePath();
    container.add(fill);

    container.add(this.add.text(0, gaugeY, `${this.metrics.dataHealth}%`, {
      fontFamily: DESIGN.fonts.headline,
      fontSize: '36px',
      color: DESIGN.colors.secondary,
      fontStyle: 'bold',
    }).setOrigin(0.5));

    container.add(this.add.text(0, gaugeY + 35, 'Health', {
      fontFamily: DESIGN.fonts.body,
      fontSize: '12px',
      color: DESIGN.colors.onSurfaceVariant,
      fontStyle: 'bold',
    }).setOrigin(0.5));

    // Data rows
    const rows = [
      { icon: '✅', label: 'Valid Sessions', value: this.metrics.validSessions, color: DESIGN.colors.secondary },
      { icon: '⚠️', label: 'Suspected Sessions', value: this.metrics.suspectedSessions, color: DESIGN.colors.tertiary },
      { icon: '🚫', label: 'Filtered Events', value: this.metrics.filteredEvents, color: DESIGN.colors.fail },
    ];

    rows.forEach((row, index) => {
      const y = 60 + index * 45;
      const rowBg = this.add.rectangle(0, y, w - 32, 36, parseInt(DESIGN.colors.surfaceContainer.replace('#', '0x'), 16));
      rowBg.setStrokeStyle(1, parseInt(DESIGN.colors.outlineVariant.replace('#', '0x'), 16), 0.3);
      container.add(rowBg);

      container.add(this.add.text(-w / 2 + 20, y, `${row.icon} ${row.label}`, {
        fontFamily: DESIGN.fonts.body,
        fontSize: '12px',
        color: DESIGN.colors.onSurfaceVariant,
      }).setOrigin(0, 0.5));

      container.add(this.add.text(w / 2 - 20, y, row.value, {
        fontFamily: DESIGN.fonts.body,
        fontSize: '13px',
        color: DESIGN.colors.onSurface,
        fontStyle: 'bold',
      }).setOrigin(1, 0.5));
    });
  }

  private addDecorativeCards(): void {
    const cards = [
      { x: 70, y: 610, title: 'Production Status', subtitle: 'Live feed from Toy Assembly Line A-12', color: 0xff7e7e },
      { x: 550, y: 610, title: 'Interactive Heatmaps', subtitle: 'Real-time player movement and grab frequency', color: 0x88f2e0 },
    ];

    cards.forEach((card) => {
      const container = this.add.container(card.x, card.y);
      const w = 460;
      const h = 120;

      const shadow = this.add.rectangle(6, 6, w, h, 0x000000, 0.08);
      container.add(shadow);

      const bg = this.add.rectangle(0, 0, w, h, card.color, 0.2);
      bg.setStrokeStyle(1, parseInt(DESIGN.colors.outlineVariant.replace('#', '0x'), 16));
      container.add(bg);

      container.add(this.add.text(-w / 2 + 20, -10, card.title, {
        fontFamily: DESIGN.fonts.headline,
        fontSize: '20px',
        color: DESIGN.colors.onSurface,
        fontStyle: 'bold',
      }).setOrigin(0, 0.5));

      container.add(this.add.text(-w / 2 + 20, 20, card.subtitle, {
        fontFamily: DESIGN.fonts.body,
        fontSize: '14px',
        color: DESIGN.colors.onSurfaceVariant,
      }).setOrigin(0, 0.5));
    });
  }

  private handleNavigation(key: string): void {
    soundManager.play('click');
    switch (key) {
      case 'play':
        transitionManager.go(this, 'GameScene');
        break;
      case 'collection':
        transitionManager.go(this, 'CollectionScene');
        break;
      case 'settings':
        transitionManager.go(this, 'SettingsScene');
        break;
      case 'debug':
        transitionManager.go(this, 'DebugPanelScene');
        break;
    }
  }
}
